import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { useAuth } from '@/hooks/useAuth';
import { useRedirectHandler } from '@/hooks/useRedirectHandler';

/**
 * The only paths an unfinished user may sit on. Every `/profile` path stays open, `/profile/:username`
 * included — that is where the Log out lives, and it is what the navbar avatar links to once a
 * username exists. Profile pages are public anyway, so allowing them here withholds nothing.
 */
const isAllowedWhileOnboarding = (pathname: string) => {
  const [first] = pathname.split('/').filter(Boolean);
  return first === 'complete-profile' || first === 'profile';
};

/**
 * Sole owner of the "finish onboarding first" redirect. Mounted at the root rather than inside
 * `ProtectedRoute` because the paths that most need gating — `/` and the `/app/*` deep links —
 * are public routes that `ProtectedRoute` never wraps.
 */
export const OnboardingGate = () => {
  const { isAuthenticated, user } = useAuth();
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const { storeRedirect } = useRedirectHandler();
  const { t: tCommon } = useTranslation('common');

  useEffect(() => {
    if (!isAuthenticated || user?.isProfileComplete !== false) return;
    if (isAllowedWhileOnboarding(pathname)) return;

    // Keep the destination: a new user usually got here by tapping a shared link, and the
    // wizard replays this on finish. Only disallowed paths reach here, so this never stores
    // onboarding onto itself.
    storeRedirect(pathname + search);
    toast.info(tCommon('completeProfile.completeFirstToExplore'));
    navigate('/complete-profile', { replace: true });
  }, [
    isAuthenticated,
    user,
    pathname,
    search,
    navigate,
    storeRedirect,
    tCommon,
  ]);

  return null;
};
